import axios from 'axios';
import {
  AUTH_VALIDATE_URL,
  CDN_BASE_URL,
  RESEARCH_DATA_URL,
  getAuthHeaders,
} from '../constants/api';
import {
  DATA_MODES,
  getDataMode,
  getDevDataUrl,
  getFixtureDataUrl,
} from './dataLoader';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableNetworkError = (error) => (
  error?.message === 'Network Error' && !error?.response
);

export const fetchAuthenticatedJsonFile = async (
  fileKey,
  token,
  { retries = 1, retryDelayMs = 300 } = {},
) => {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const params = { fileKey };
      if (attempt > 0) {
        params._retry = `${Date.now()}-${attempt}`;
      }

      const response = await axios.get(AUTH_VALIDATE_URL, {
        headers: getAuthHeaders(token),
        params,
      });

      return response.data?.data ?? response.data;
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isRetryableNetworkError(error)) {
        throw error;
      }
      await delay(retryDelayMs);
    }
  }

  throw lastError;
};

export const fetchResearchJsonFile = async (
  fileKey,
  token,
  options = {},
) => {
  const { retries = 1, retryDelayMs = 300 } = options;
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await axios.get(RESEARCH_DATA_URL, {
        headers: getAuthHeaders(token),
        params: {
          fileKey,
          ...(attempt > 0 ? { _retry: `${Date.now()}-${attempt}` } : {}),
        },
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isRetryableNetworkError(error)) throw error;
      await delay(retryDelayMs);
    }
  }

  throw lastError;
};

export const downloadResearchFile = async (fileKey, token) => {
  const dataMode = getDataMode();
  if (dataMode === DATA_MODES.LOCAL || dataMode === DATA_MODES.FIXTURE) {
    const localPath = `data/${String(fileKey || '').replace(/^\/+/, '')}`;
    const localUrl = dataMode === DATA_MODES.FIXTURE
      ? getFixtureDataUrl(localPath)
      : getDevDataUrl(localPath);
    const localResponse = await axios.get(localUrl, { responseType: 'blob' });
    return localResponse.data;
  }

  const response = await axios.get(RESEARCH_DATA_URL, {
    headers: {
      ...getAuthHeaders(token),
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    params: { fileKey },
    responseType: 'blob',
  });

  return response.data;
};

export const downloadPublicFile = async (fileKey) => {
  const dataMode = getDataMode();
  const normalizedKey = String(fileKey || '').replace(/^\/+/, '');
  const url = dataMode === DATA_MODES.FIXTURE
    ? getFixtureDataUrl(`data/${normalizedKey}`)
    : dataMode === DATA_MODES.LOCAL
      ? getDevDataUrl(`data/${normalizedKey}`)
      : `${CDN_BASE_URL}/${normalizedKey}`;
  const response = await axios.get(url, { responseType: 'blob' });
  return response.data;
};
